<template>
  <div class="dias-libres-view">
    <!-- Subtabs Navigation -->
    <div class="d-flex align-items-center mb-1">
      <button
        type="button"
        :class="['subtab-btn', activeSubtab === 'control_mes' ? 'subtab-active' : 'subtab-inactive']"
        @click="activeSubtab = 'control_mes'"
      >
        <feather-icon icon="CalendarIcon" size="14" class="mr-50" />
        Control del mes
      </button>
      <button
        type="button"
        :class="['subtab-btn ml-75', activeSubtab === 'solicitudes_venta' ? 'subtab-active' : 'subtab-inactive']"
        @click="activeSubtab = 'solicitudes_venta'"
      >
        <feather-icon icon="FileTextIcon" size="14" class="mr-50" />
        Solicitudes de venta
        <span
          v-if="solicitudesPendientesTotal > 0"
          class="badge-pending ml-50"
        >
          {{ solicitudesPendientesTotal }}
        </span>
      </button>
    </div>

    <!-- SUBTAB 1: CONTROL DEL MES -->
    <div v-show="activeSubtab === 'control_mes'">
      <!-- Header with Title, Description and Filters -->
      <div class="header-card mb-1">
        <div class="d-flex flex-wrap justify-content-between align-items-center">
          <!-- Title & Link -->
          <div class="mb-1 mb-lg-0">
            <h2 class="page-title mb-25">
              Dias libres de conductores
            </h2>
            <div class="page-subtitle">
              <span>Cuántos días libres genera, toma, vende o pierde cada conductor y cuánto le queda.</span>
              <span class="calc-link ml-50" @click="openComoSeCalcula">
                <feather-icon icon="HelpCircleIcon" size="14" class="mr-25 text-info" />
                <span class="text-info font-weight-bold">¿Cómo se calcula?</span>
              </span>
            </div>
          </div>

          <!-- Filters: Mes, Año, Área, Excel -->
          <div class="d-flex flex-wrap align-items-end">
            <!-- Mes -->
            <div class="filter-group mr-1">
              <label class="filter-label">Mes</label>
              <select
                v-model="selectedMes"
                class="filter-select"
                style="width: 120px;"
                @change="loadData"
              >
                <option v-for="m in mesesOptions" :key="m.value" :value="m.value">
                  {{ m.text }}
                </option>
              </select>
            </div>

            <!-- Año -->
            <div class="filter-group mr-1">
              <label class="filter-label">Año</label>
              <select
                v-model="selectedAnio"
                class="filter-select"
                style="width: 80px;"
                @change="loadData"
              >
                <option v-for="a in aniosOptions" :key="a" :value="a">
                  {{ a }}
                </option>
              </select>
            </div>

            <!-- Área -->
            <div class="filter-group mr-1">
              <label class="filter-label">Área</label>
              <select
                v-model="selectedArea"
                class="filter-select"
                style="width: 150px;"
                @change="loadData"
              >
                <option :value="null">Todas las áreas</option>
                <option v-for="area in areas" :key="area.id" :value="area.id">
                  {{ area.name || area.nombre }}
                </option>
              </select>
            </div>

            <!-- Excel Button -->
            <div class="filter-group">
              <button
                type="button"
                class="btn-excel"
                :disabled="loading || exportingExcel"
                @click="exportExcel"
              >
                <feather-icon v-if="!exportingExcel" icon="DownloadIcon" size="13" class="mr-50" />
                <span v-else class="spinner-border spinner-border-sm mr-50" role="status" />
                Excel
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- WARNING BANNER: Conductores con datos pendientes -->
      <div v-if="pendientesDatos.length > 0" class="banner-warning mb-1">
        <div class="d-flex align-items-center">
          <feather-icon icon="AlertTriangleIcon" size="18" class="mr-75 banner-warning-icon" />
          <span class="banner-warning-text">
            {{ pendientesDatos.length }} conductor(es) no generan dias libres porque les falta el régimen o el sueldo.
          </span>
        </div>
        <button type="button" class="btn-warning-action" @click="openCompletarDatos">
          Completar datos
        </button>
      </div>

      <!-- DANGER BANNER: Conductores con saldo negativo -->
      <div v-if="conductoresConSaldoNegativo.length > 0" class="banner-danger mb-1">
        <div class="d-flex align-items-center">
          <feather-icon icon="TrendingDownIcon" size="18" class="mr-75 banner-danger-icon" />
          <span class="banner-danger-text">
            {{ conductoresConSaldoNegativo.length }} conductor(es) tienen saldo negativo: tomaron más dias de los que generaron. Revisa su historial.
          </span>
        </div>
        <button type="button" class="btn-danger-action" @click="filterOnlyNegative">
          {{ filterNegativeActive ? 'Ver todos' : 'Ver conductores' }}
        </button>
      </div>

      <!-- 6 KPI METRICS ROW (Single Container Matching Image 1) -->
      <div class="metrics-container mb-2">
        <div class="row no-gutters">
          <!-- 1. CONDUCTORES -->
          <div class="col-12 col-md-6 col-lg-2 metric-col">
            <div class="d-flex justify-content-between align-items-start h-100">
              <div class="metric-text-wrap">
                <span class="metric-title">CONDUCTORES</span>
                <div class="metric-value">{{ metrics.totalConductores }}</div>
                <div class="metric-subtitle">
                  {{ metrics.totalPlanilla }} Planilla · {{ metrics.totalRxh }} R x H · {{ metrics.totalDoble }} doble planilla
                </div>
              </div>
              <div class="metric-icon-circle icon-blue">
                <feather-icon icon="UsersIcon" size="18" />
              </div>
            </div>
          </div>

          <!-- 2. SALDO ACUMULADO -->
          <div class="col-12 col-md-6 col-lg-2 metric-col">
            <div class="d-flex justify-content-between align-items-start h-100">
              <div class="metric-text-wrap">
                <span class="metric-title">SALDO ACUMULADO</span>
                <div class="metric-value">
                  {{ metrics.saldoAcumulado }}
                  <span class="metric-unit">días</span>
                </div>
                <div class="metric-subtitle">
                  Promedio de {{ metrics.promedioSaldo }} días por conductor
                </div>
              </div>
              <div class="metric-icon-circle icon-purple">
                <feather-icon icon="CalendarIcon" size="18" />
              </div>
            </div>
          </div>

          <!-- 3. TOMADOS EN [MES] -->
          <div class="col-12 col-md-6 col-lg-2 metric-col">
            <div class="d-flex justify-content-between align-items-start h-100">
              <div class="metric-text-wrap">
                <span class="metric-title">TOMADOS EN {{ nombreMesActual.toUpperCase() }}</span>
                <div class="metric-value">
                  {{ metrics.diasTomados }}
                  <span class="metric-unit">de {{ metrics.diasMeta }}</span>
                </div>
                <div class="progress-bar-wrap">
                  <div class="progress-bar-fill" :style="{ width: metrics.pctTomados + '%' }" />
                </div>
                <div class="metric-subtitle">
                  {{ metrics.pctTomados }}% de la meta del mes
                </div>
              </div>
              <div class="metric-icon-circle icon-green">
                <feather-icon icon="SunIcon" size="18" />
              </div>
            </div>
          </div>

          <!-- 4. VENDIDOS EN [MES] -->
          <div class="col-12 col-md-6 col-lg-2 metric-col">
            <div class="d-flex justify-content-between align-items-start h-100">
              <div class="metric-text-wrap">
                <span class="metric-title">VENDIDOS EN {{ nombreMesActual.toUpperCase() }}</span>
                <div class="metric-value">
                  {{ metrics.diasVendidos }}
                  <span class="metric-unit">días</span>
                </div>
                <div class="metric-subtitle">
                  <span v-if="metrics.solicitudesPendientes > 0" class="text-warning font-weight-bold">
                    {{ metrics.solicitudesPendientes }} pendientes
                  </span>
                  <span v-else>Sin solicitudes pendientes</span>
                </div>
              </div>
              <div class="metric-icon-circle icon-orange">
                <feather-icon icon="DollarSignIcon" size="18" />
              </div>
            </div>
          </div>

          <!-- 5. PERDIDOS EN [MES] -->
          <div class="col-12 col-md-6 col-lg-2 metric-col">
            <div class="d-flex justify-content-between align-items-start h-100">
              <div class="metric-text-wrap">
                <span class="metric-title">PERDIDOS EN {{ nombreMesActual.toUpperCase() }}</span>
                <div class="metric-value">
                  {{ metrics.diasPerdidos }}
                  <span class="metric-unit">días</span>
                </div>
                <div class="metric-subtitle">
                  Por permisos o faltas
                </div>
              </div>
              <div class="metric-icon-circle icon-red">
                <feather-icon icon="SlashIcon" size="18" />
              </div>
            </div>
          </div>

          <!-- 6. SALDO NEGATIVO -->
          <div
            class="col-12 col-md-6 col-lg-2 metric-col cursor-pointer"
            :class="{ 'metric-col-active': filterNegativeActive }"
            @click="filterOnlyNegative"
          >
            <div class="d-flex justify-content-between align-items-start h-100">
              <div class="metric-text-wrap">
                <span class="metric-title">SALDO NEGATIVO</span>
                <div class="metric-value">
                  {{ metrics.saldoNegativoCount }}
                  <span class="metric-unit">conductores</span>
                </div>
                <div class="metric-subtitle text-danger font-weight-500">
                  {{ filterNegativeActive ? '✕ Quitar filtro' : 'Clic para verlos en la tabla' }}
                </div>
              </div>
              <div class="metric-icon-circle icon-red">
                <feather-icon icon="TrendingDownIcon" size="18" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- CONDUCTORS TABLE CARD -->
      <div class="table-card">
        <!-- Table Header Bar -->
        <div class="table-header-bar d-flex flex-wrap justify-content-between align-items-center">
          <div class="d-flex align-items-center">
            <h4 class="table-title mb-0">
              Conductores - {{ nombreMesActual }} {{ selectedAnio }}
            </h4>
            <span
              v-if="filterNegativeActive"
              class="badge badge-light-danger ml-1 cursor-pointer font-weight-bold"
              @click="filterNegativeActive = false"
            >
              Filtro: Saldo Negativo ✕
            </span>
          </div>

          <!-- Search Input -->
          <div class="search-wrap">
            <input
              v-model="searchTerm"
              type="text"
              class="search-input"
              placeholder="Buscar por nombre, documento o empresa..."
              @input="currentPage = 1"
            >
            <feather-icon icon="SearchIcon" size="14" class="search-icon" />
          </div>
        </div>

        <!-- Table Responsive -->
        <div class="table-responsive">
          <table class="table custom-conductores-table mb-0">
            <thead>
              <tr>
                <th class="text-left" style="width: 22%;">CONDUCTOR</th>
                <th class="text-left" style="width: 10%;">ÁREA</th>
                <th class="text-left" style="width: 9%;">CARGO</th>
                <th class="text-left" style="width: 9%;">F. INGRESO</th>
                <th class="text-left" style="width: 10%;">SUELDO</th>
                <th class="text-center" style="width: 9%;">RÉGIMEN</th>
                <th class="text-center" style="width: 6%;">DÍAS META</th>
                <th class="text-center" style="width: 6%;">DÍAS TOMADOS</th>
                <th class="text-center" style="width: 6%;">DÍAS VENDIDOS</th>
                <th class="text-center" style="width: 6%;">DÍAS PERDIDOS</th>
                <th class="text-center" style="width: 9%;">SALDO PENDIENTE</th>
                <th class="text-center" style="width: 14%;">ACCIONES</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="12" class="text-center py-3">
                  <div class="spinner-border spinner-border-sm text-primary mr-50" role="status" />
                  <span class="text-muted font-weight-bold">Cargando reporte de días libres...</span>
                </td>
              </tr>
              <tr v-else-if="filteredConductores.length === 0">
                <td colspan="12" class="text-center py-3 text-muted">
                  No se encontraron conductores registrados.
                </td>
              </tr>
              <tr v-for="item in paginatedConductores" :key="item.personal_id">
                <!-- CONDUCTOR (Plain name, no avatar, matching Image 1) -->
                <td class="text-left">
                  <span class="conductor-name">{{ item.empleado }}</span>
                </td>

                <!-- ÁREA -->
                <td class="text-left text-muted">
                  {{ item.area || '-' }}
                </td>

                <!-- CARGO -->
                <td class="text-left text-muted">
                  {{ item.cargo || '-' }}
                </td>

                <!-- F. INGRESO -->
                <td class="text-left text-muted">
                  {{ formatDate(item.fecha_ingreso) }}
                </td>

                <!-- SUELDO -->
                <td class="text-left">
                  <span class="font-weight-500">S/ {{ formatMoney(item.sueldo) }}</span>
                  <span v-if="item.doble_planilla" class="badge-doble ml-50">Doble</span>
                </td>

                <!-- RÉGIMEN -->
                <td class="text-center">
                  <span :class="item.tiene_regimen ? 'regimen-badge' : 'regimen-sin-badge'">
                    {{ item.regimen || 'SIN RÉGIMEN' }}
                  </span>
                </td>

                <!-- DÍAS META -->
                <td class="text-center">
                  <span class="val-meta">{{ item.dias_meta || 0 }}</span>
                </td>

                <!-- DÍAS TOMADOS -->
                <td class="text-center">
                  <span class="val-tomados">{{ item.dias_tomados || 0 }}</span>
                </td>

                <!-- DÍAS VENDIDOS -->
                <td class="text-center">
                  <span class="val-vendidos">{{ item.dias_vendidos || 0 }}</span>
                </td>

                <!-- DÍAS PERDIDOS -->
                <td class="text-center">
                  <span class="val-perdidos">{{ item.dias_perdidos || 0 }}</span>
                </td>

                <!-- SALDO PENDIENTE (Formatted with +, -, 2 decimals) -->
                <td class="text-center">
                  <span :class="getSaldoBadgeClass(item.saldo_pendiente)">
                    {{ formatSaldo(item.saldo_pendiente) }}
                  </span>
                </td>

                <!-- ACCIONES -->
                <td class="text-center text-nowrap">
                  <button
                    type="button"
                    class="btn-action-vender"
                    @click="openVenderModal(item)"
                  >
                    💲 VENDER DÍAS
                  </button>
                  <button
                    type="button"
                    class="btn-action-historial ml-50"
                    @click="openHistorialModal(item)"
                  >
                    <feather-icon icon="ClockIcon" size="12" class="mr-25" />
                    HISTORIAL
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Footer with Pagination -->
        <div class="table-footer-bar d-flex justify-content-between align-items-center flex-wrap">
          <small class="text-muted">
            Mostrando {{ filteredConductores.length ? (currentPage - 1) * perPage + 1 : 0 }} a
            {{ Math.min(currentPage * perPage, filteredConductores.length) }} de {{ filteredConductores.length }} conductores
          </small>
          <b-pagination
            v-if="filteredConductores.length > perPage"
            v-model="currentPage"
            :total-rows="filteredConductores.length"
            :per-page="perPage"
            align="right"
            class="mb-0"
          />
        </div>
      </div>
    </div>

    <!-- SUBTAB 2: SOLICITUDES DE VENTA -->
    <div v-show="activeSubtab === 'solicitudes_venta'">
      <solicitudes-venta-tab
        ref="solicitudesTab"
        @solicitud-procesada="loadData"
      />
    </div>

    <!-- MODALS -->
    <como-se-calcula-modal />
    <completar-datos-modal ref="completarDatosModal" @saved="onDatosCompletados" />
    <historial-dias-libres-modal ref="historialModal" />
    <solicitud-venta-modal ref="solicitudVentaModal" @saved="onVentaRegistrada" />
  </div>
</template>

<script>
import { BPagination } from 'bootstrap-vue';
import DiasLibresService from './service/dias-libres.service';
import EmployeeService from '@/views/rrhh/views/employees/services/employee.service.js';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';

import ComoSeCalculaModal from './components/ComoSeCalculaModal.vue';
import CompletarDatosModal from './components/CompletarDatosModal.vue';
import HistorialDiasLibresModal from './components/HistorialDiasLibresModal.vue';
import SolicitudVentaModal from './components/SolicitudVentaModal.vue';
import SolicitudesVentaTab from './components/SolicitudesVentaTab.vue';

export default {
  name: 'DiasLibres',
  components: {
    BPagination,
    ComoSeCalculaModal,
    CompletarDatosModal,
    HistorialDiasLibresModal,
    SolicitudVentaModal,
    SolicitudesVentaTab,
  },
  data() {
    const now = new Date();
    return {
      activeSubtab: 'control_mes', // 'control_mes' | 'solicitudes_venta'
      loading: false,
      exportingExcel: false,

      // Filters
      selectedMes: now.getMonth() + 1,
      selectedAnio: now.getFullYear(),
      selectedArea: null,
      searchTerm: '',
      filterNegativeActive: false,

      // Data
      reporte: [],
      pendientesDatos: [],
      areas: [],
      solicitudesPendientesTotal: 0,

      // Pagination
      currentPage: 1,
      perPage: 15,

      // Options
      aniosOptions: [2024, 2025, 2026, 2027],
      mesesOptions: [
        { value: 1, text: 'Enero' },
        { value: 2, text: 'Febrero' },
        { value: 3, text: 'Marzo' },
        { value: 4, text: 'Abril' },
        { value: 5, text: 'Mayo' },
        { value: 6, text: 'Junio' },
        { value: 7, text: 'Julio' },
        { value: 8, text: 'Agosto' },
        { value: 9, text: 'Setiembre' },
        { value: 10, text: 'Octubre' },
        { value: 11, text: 'Noviembre' },
        { value: 12, text: 'Diciembre' },
      ],
    };
  },
  computed: {
    nombreMesActual() {
      const m = this.mesesOptions.find((x) => x.value === this.selectedMes);
      return m ? m.text : '';
    },
    conductoresConSaldoNegativo() {
      return this.reporte.filter((c) => Number(c.saldo_pendiente) < 0);
    },
    metrics() {
      const totalConductores = this.reporte.length;
      let totalPlanilla = 0;
      let totalRxh = 0;
      let totalDoble = 0;
      let saldoAcumulado = 0;
      let diasTomados = 0;
      let diasMeta = 0;
      let diasVendidos = 0;
      let diasPerdidos = 0;
      let solicitudesPendientes = 0;

      this.reporte.forEach((c) => {
        const tipo = (c.tipo_trabajador || '').toLowerCase();
        if (tipo.includes('planilla')) totalPlanilla++;
        if (tipo.includes('recibo') || tipo.includes('r x h') || tipo.includes('rxh')) totalRxh++;
        if (c.doble_planilla) totalDoble++;

        saldoAcumulado += Number(c.saldo_pendiente) || 0;
        diasTomados += Number(c.dias_tomados) || 0;
        diasMeta += Number(c.dias_meta) || 0;
        diasVendidos += Number(c.dias_vendidos) || 0;
        diasPerdidos += Number(c.dias_perdidos) || 0;
        solicitudesPendientes += Number(c.solicitudes_pendientes) || 0;
      });

      const promedioSaldo = totalConductores > 0 ? (saldoAcumulado / totalConductores).toFixed(2) : '0.00';
      const pctTomados = diasMeta > 0 ? Math.round((diasTomados / diasMeta) * 100) : 0;
      const saldoNegativoCount = this.conductoresConSaldoNegativo.length;

      return {
        totalConductores,
        totalPlanilla,
        totalRxh,
        totalDoble,
        saldoAcumulado: saldoAcumulado.toFixed(2),
        promedioSaldo,
        diasTomados: Number(diasTomados.toFixed(2)),
        diasMeta: Number(diasMeta.toFixed(2)),
        pctTomados: Math.min(pctTomados, 100),
        diasVendidos: Number(diasVendidos.toFixed(2)),
        solicitudesPendientes,
        diasPerdidos: Number(diasPerdidos.toFixed(2)),
        saldoNegativoCount,
      };
    },
    filteredConductores() {
      let list = this.reporte;

      if (this.filterNegativeActive) {
        list = list.filter((c) => Number(c.saldo_pendiente) < 0);
      }

      if (this.searchTerm && this.searchTerm.trim()) {
        const q = this.searchTerm.trim().toLowerCase();
        list = list.filter(
          (c) =>
            (c.empleado && c.empleado.toLowerCase().includes(q)) ||
            (c.documento && c.documento.includes(q)) ||
            (c.area && c.area.toLowerCase().includes(q)) ||
            (c.cargo && c.cargo.toLowerCase().includes(q)) ||
            (c.empresa && c.empresa.toLowerCase().includes(q))
        );
      }

      return list;
    },
    paginatedConductores() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredConductores.slice(start, start + this.perPage);
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    async init() {
      await Promise.all([this.loadAreas(), this.loadData(), this.loadPendientesDatos()]);
    },
    async loadAreas() {
      try {
        const res = await EmployeeService.getAllAreas({ status: 1 });
        if (res.data) {
          this.areas = Array.isArray(res.data) ? res.data : res.data.data || [];
        }
      } catch (e) {
        // Fallback
      }
    },
    async loadPendientesDatos() {
      try {
        const res = await DiasLibresService.getDatosPendientes();
        if (res.data) {
          this.pendientesDatos = res.data.pendientes || [];
        }
      } catch (e) {
        console.error('Error al cargar datos pendientes:', e);
      }
    },
    async loadData() {
      this.loading = true;
      try {
        const params = {
          mes: this.selectedMes,
          anio: this.selectedAnio,
          area_id: this.selectedArea,
        };
        const res = await DiasLibresService.getReporteMensual(params);
        if (res.data) {
          this.reporte = Array.isArray(res.data) ? res.data : [];
          this.solicitudesPendientesTotal = this.reporte.reduce(
            (acc, c) => acc + (Number(c.solicitudes_pendientes) || 0),
            0
          );
        }
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertCircleIcon',
            text: e.response?.data?.message || 'Error al obtener el reporte de días libres.',
            variant: 'danger',
          },
        });
      } finally {
        this.loading = false;
      }
    },
    filterOnlyNegative() {
      this.filterNegativeActive = !this.filterNegativeActive;
      this.currentPage = 1;
    },
    openComoSeCalcula() {
      this.$bvModal.show('modal-como-se-calcula');
    },
    openCompletarDatos() {
      if (this.$refs.completarDatosModal) {
        this.$refs.completarDatosModal.open();
      }
    },
    onDatosCompletados() {
      this.loadData();
      this.loadPendientesDatos();
    },
    openVenderModal(conductor) {
      if (this.$refs.solicitudVentaModal) {
        this.$refs.solicitudVentaModal.open(conductor);
      }
    },
    openHistorialModal(conductor) {
      if (this.$refs.historialModal) {
        this.$refs.historialModal.open(conductor, this.selectedAnio);
      }
    },
    onVentaRegistrada() {
      this.loadData();
      if (this.$refs.solicitudesTab) {
        this.$refs.solicitudesTab.loadSolicitudes();
      }
    },
    async exportExcel() {
      this.exportingExcel = true;
      try {
        const params = {
          mes: this.selectedMes,
          anio: this.selectedAnio,
          area_id: this.selectedArea,
        };
        const res = await DiasLibresService.exportToExcel(params);
        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = `Reporte_Dias_Libres_${this.selectedAnio}_${String(this.selectedMes).padStart(2, '0')}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(link.href);

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Descarga exitosa',
            icon: 'CheckIcon',
            text: 'El reporte de días libres fue descargado.',
            variant: 'success',
          },
        });
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error al exportar',
            icon: 'AlertCircleIcon',
            text: 'No se pudo exportar el reporte en Excel.',
            variant: 'danger',
          },
        });
      } finally {
        this.exportingExcel = false;
      }
    },
    formatSaldo(saldo) {
      const num = Number(saldo || 0);
      if (num > 0) return `+${num.toFixed(2)}`;
      if (num < 0) return num.toFixed(2);
      return '0';
    },
    getSaldoBadgeClass(saldo) {
      const num = Number(saldo || 0);
      if (num > 0) return 'saldo-badge-positive';
      if (num < 0) return 'saldo-badge-negative';
      return 'saldo-badge-zero';
    },
    formatDate(dateStr) {
      if (!dateStr) return '-';
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
      return dateStr;
    },
    formatMoney(val) {
      return parseFloat(val || 0).toLocaleString('es-PE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },
};
</script>

<style scoped>
/* SUBTABS */
.subtab-btn {
  border: none;
  font-size: 13px;
  font-weight: 600;
  border-radius: 6px;
  padding: 7px 16px;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s;
  outline: none;
}
.subtab-active {
  background-color: #0091ff !important;
  color: #ffffff !important;
  box-shadow: 0 2px 4px rgba(0, 145, 255, 0.2);
}
.subtab-inactive {
  background-color: transparent !important;
  color: #64748b !important;
}
.subtab-inactive:hover {
  background-color: rgba(0, 0, 0, 0.04) !important;
  color: #1e293b !important;
}
.badge-pending {
  background-color: #ff9f43;
  color: #ffffff;
  border-radius: 10px;
  font-size: 11px;
  padding: 2px 6px;
  font-weight: 700;
}

/* HEADER CARD */
.header-card {
  background: #ffffff;
  border-radius: 8px;
  padding: 18px 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}
.page-title {
  color: #1e293b;
  font-weight: 700;
  font-size: 20px;
  letter-spacing: -0.3px;
}
.page-subtitle {
  color: #64748b;
  font-size: 13px;
}
.calc-link {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
}
.calc-link:hover span {
  text-decoration: underline;
}

/* FILTERS */
.filter-group {
  display: flex;
  flex-direction: column;
}
.filter-label {
  font-size: 11px;
  font-weight: 600;
  color: #8c8c8c;
  margin-bottom: 3px;
}
.filter-select {
  height: 32px;
  border-radius: 6px;
  border: 1px solid #d9d9d9;
  padding: 2px 8px;
  font-size: 12px;
  color: #333333;
  background-color: #ffffff;
  outline: none;
}
.filter-select:focus {
  border-color: #0091ff;
}
.btn-excel {
  height: 32px;
  border-radius: 6px;
  border: 1px solid #28c76f;
  background-color: #ffffff;
  color: #28c76f;
  font-size: 12px;
  font-weight: 600;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
}
.btn-excel:hover {
  background-color: #28c76f;
  color: #ffffff;
}

/* BANNERS */
.banner-warning {
  background-color: #fff7ed;
  border: 1px solid #ffedd5;
  border-radius: 6px;
  padding: 10px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.banner-warning-icon {
  color: #f97316;
}
.banner-warning-text {
  color: #ea580c;
  font-weight: 600;
  font-size: 13px;
}
.btn-warning-action {
  background-color: #ff9f43;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  padding: 6px 16px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.btn-warning-action:hover {
  background-color: #f58e2a;
}

.banner-danger {
  background-color: #fff1f2;
  border: 1px solid #ffe4e6;
  border-radius: 6px;
  padding: 10px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.banner-danger-icon {
  color: #ef4444;
}
.banner-danger-text {
  color: #dc2626;
  font-weight: 600;
  font-size: 13px;
}
.btn-danger-action {
  background-color: #ea5455;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  padding: 6px 16px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.btn-danger-action:hover {
  background-color: #d93d3e;
}

/* 6 KPI METRICS CONTAINER */
.metrics-container {
  background: #ffffff;
  border-radius: 8px;
  padding: 18px 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}
.metric-col {
  padding: 0 16px;
  border-right: 1px solid #f1f5f9;
}
.metric-col:last-child {
  border-right: none;
}
.metric-col:first-child {
  padding-left: 0;
}
.metric-col-active {
  background-color: #fff1f2;
  border-radius: 6px;
}
.metric-text-wrap {
  flex: 1;
  min-width: 0;
  padding-right: 8px;
}
.metric-title {
  color: #8c8c8c;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  display: block;
}
.metric-value {
  font-size: 26px;
  font-weight: 800;
  color: #1e293b;
  line-height: 1.15;
  margin: 4px 0 2px 0;
}
.metric-unit {
  font-size: 12px;
  font-weight: 400;
  color: #94a3b8;
  margin-left: 3px;
}
.metric-subtitle {
  color: #94a3b8;
  font-size: 11px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.progress-bar-wrap {
  height: 5px;
  background-color: #e2e8f0;
  border-radius: 3px;
  overflow: hidden;
  margin: 6px 0 4px 0;
}
.progress-bar-fill {
  height: 100%;
  background-color: #22c55e;
  border-radius: 3px;
  transition: width 0.3s;
}

/* METRIC ICONS */
.metric-icon-circle {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.icon-blue {
  background-color: #e0f2fe;
  color: #0284c7;
}
.icon-purple {
  background-color: #ede9fe;
  color: #7c3aed;
}
.icon-green {
  background-color: #dcfce7;
  color: #16a34a;
}
.icon-orange {
  background-color: #fef3c7;
  color: #d97706;
}
.icon-red {
  background-color: #fee2e2;
  color: #dc2626;
}

/* TABLE CARD */
.table-card {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}
.table-header-bar {
  padding: 16px 22px;
  border-bottom: 1px solid #f1f5f9;
}
.table-title {
  color: #1e293b;
  font-weight: 700;
  font-size: 15px;
}
.search-wrap {
  position: relative;
  width: 290px;
}
.search-input {
  width: 100%;
  height: 32px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  padding: 4px 30px 4px 10px;
  font-size: 12px;
  color: #334155;
  outline: none;
}
.search-input:focus {
  border-color: #0091ff;
}
.search-icon {
  position: absolute;
  right: 10px;
  top: 9px;
  color: #94a3b8;
  pointer-events: none;
}

/* CUSTOM TABLE */
.custom-conductores-table {
  width: 100%;
}
.custom-conductores-table thead th {
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.4px;
  border-bottom: 1px solid #f1f5f9;
  border-top: none;
  padding: 12px 10px;
  background: transparent;
  vertical-align: middle;
}
.custom-conductores-table tbody tr {
  border-bottom: 1px solid #f8fafc;
}
.custom-conductores-table tbody tr:hover {
  background-color: #f8fafc;
}
.custom-conductores-table tbody td {
  padding: 11px 10px;
  vertical-align: middle;
  font-size: 12px;
  border-top: none;
}

.conductor-name {
  font-weight: 700;
  color: #1e293b;
  font-size: 12px;
}
.badge-doble {
  background-color: #e0f2fe;
  color: #0284c7;
  font-size: 10px;
  font-weight: 700;
  border-radius: 4px;
  padding: 2px 6px;
}

/* REGIMEN BADGES */
.regimen-badge {
  background-color: #f3e8ff;
  color: #7e22ce;
  font-size: 11px;
  font-weight: 700;
  border-radius: 6px;
  padding: 3px 10px;
  display: inline-block;
}
.regimen-sin-badge {
  background-color: #ffedd5;
  color: #ea580c;
  font-size: 11px;
  font-weight: 700;
  border-radius: 6px;
  padding: 3px 10px;
  display: inline-block;
}

/* CELL VALUES */
.val-meta {
  color: #0284c7;
  font-weight: 700;
  font-size: 13px;
}
.val-tomados {
  color: #16a34a;
  font-weight: 700;
  font-size: 13px;
}
.val-vendidos {
  color: #ea580c;
  font-weight: 700;
  font-size: 13px;
}
.val-perdidos {
  color: #64748b;
  font-weight: 600;
  font-size: 13px;
}

/* SALDO BADGES */
.saldo-badge-positive {
  background-color: #dcfce7;
  color: #15803d;
  font-weight: 700;
  font-size: 12px;
  border-radius: 6px;
  padding: 4px 12px;
  display: inline-block;
}
.saldo-badge-negative {
  background-color: #fee2e2;
  color: #b91c1c;
  font-weight: 700;
  font-size: 12px;
  border-radius: 6px;
  padding: 4px 12px;
  display: inline-block;
}
.saldo-badge-zero {
  background-color: #f1f5f9;
  color: #475569;
  font-weight: 700;
  font-size: 12px;
  border-radius: 6px;
  padding: 4px 12px;
  display: inline-block;
}

/* ACTION BUTTONS */
.btn-action-vender {
  background-color: #0091ff;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  padding: 5px 12px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
  cursor: pointer;
  transition: background-color 0.15s;
}
.btn-action-vender:hover {
  background-color: #0080e0;
}
.btn-action-historial {
  background-color: #ffffff;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 5px 12px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-action-historial:hover {
  background-color: #f9fafb;
  border-color: #9ca3af;
}

.table-footer-bar {
  padding: 14px 22px;
  border-top: 1px solid #f1f5f9;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
